import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import {
  Car,
  ArrowRight,
  Loader,
  AlertCircle,
  Check,
  MapPin,
  Hash,
  Gauge,
  KeyRound,
  ListChecks,
  CreditCard,
  ChevronDown,
  Camera,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";
import { ExtendedWarrantyButton } from "../extended-warranty/ExtendedWarrantyScreenWrapper";
import { MOCK_US_STATES } from "../PostAuthenticationSurvey";
import VehicleInfoService from "../../lib/VehicleInfoService";
import analyticsService from "../../lib/AnalyticsService";
import insurancePolicyService from "../../lib/InsurancePolicyService";
import DocumentPreviewModal from "../DocumentPreviewModal";

const INPUT_METHODS = {
  VIN: "vin",
  MAKE_MODEL_YEAR: "make-model-year",
  LICENSE_PLATE: "license-plate",
};

const RefinancingVehicleSelection = ({ onComplete, onNoVehicles }) => {
  const { user, getIdToken } = useAuth();

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasExistingPolicy, setHasExistingPolicy] = useState(false);

  // Manual entry state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [inputMethod, setInputMethod] = useState(null);
  const [manualData, setManualData] = useState({
    vin: "",
    year: "",
    make: "",
    model: "",
    trim: "",
    nadaUid: "",
    licensePlate: "",
    state: "",
  });
  const [manualErrors, setManualErrors] = useState({});
  const [openPreviewModal, setOpenPreviewModal] = useState(false);

  // Caribou YMMT cascade data
  const [caribouOptions, setCaribouOptions] = useState({
    years: [],
    makes: [],
    models: [],
    trims: [],
  });
  const [loadingStates, setLoadingStates] = useState({
    years: false,
    makes: false,
    models: false,
    trims: false,
  });

  // --- Helpers --------------------------------------------------------------
  const toStr = (v) => (typeof v === "string" ? v.trim() : v);

  const normalize = (raw = {}) => {
    const v = raw || {};
    const year = v.year ?? v.Year ?? null;
    const make = toStr(v.make ?? v.Make ?? v.brand ?? null);
    const model = toStr(v.model ?? v.Model ?? null);
    const state = toStr(v.state ?? v.State ?? v.registrationState ?? null);
    const plate = toStr(
      v.license ?? v.licensePlate ?? v.plate ?? v.registrationNumber ?? null
    );
    const vin = toStr(v.vin ?? v.VIN ?? v.vinNumber ?? null);
    const mileage =
      typeof v.mileage === "number"
        ? v.mileage
        : typeof v.mileage === "string"
        ? Number(v.mileage.replace(/[,\s]/g, "")) || null
        : null;
    const source = v.source ?? v.Source ?? null;
    const id = v.id ?? v.vehicleId ?? v._id ?? null;
    const trim = toStr(v.trim ?? v.Trim ?? null);
    const nadaUid = v.nadaUid ?? v.uid ?? null;

    return {
      id,
      year,
      make,
      model,
      trim,
      nadaUid,
      state,
      plate,
      vin,
      mileage,
      source,
      _raw: v,
    };
  };

  const lastVin = (vin) => (vin ? vin.slice(-6).toUpperCase() : null);

  const titleFor = (n) => {
    const y = n.year ? String(n.year) : null;
    const base = [y, n.make, n.model].filter(Boolean).join(" ");
    if (base) return base;
    const mm = [n.make, n.model].filter(Boolean).join(" ");
    if (mm) return mm;
    if (n.model) return n.model;
    if (n.vin) return `VIN \u2022 \u2026${lastVin(n.vin)}`;
    if (n.plate && n.state) return `${n.plate} \u2022 ${n.state}`;
    if (n.plate) return n.plate;
    if (n.state) return `Vehicle in ${n.state}`;
    return "Vehicle";
  };

  const subtitleFor = (n) => {
    const bits = [];
    if (n.plate) bits.push(n.plate);
    if (n.state) bits.push(n.state);
    if (n.mileage != null) bits.push(`${n.mileage.toLocaleString()} miles`);
    return bits.join(" \u2022 ");
  };

  const keyFor = (n, idx) =>
    n.id || n.vin || n.plate || `${n.make}-${n.model}-${n.year}-${idx}`;
  const sameVehicle = (a, b) => {
    if (!a || !b) return false;
    if (a.id && b.id) return a.id === b.id;
    if (a.vin && b.vin) return a.vin === b.vin;
    if (a.plate && b.plate) return a.plate === b.plate;
    return a === b;
  };

  // Page view tracking
  useEffect(() => {
    analyticsService.trackPageView("refi_vehicle_selection", {
      screen: "vehicle_selection",
      flow_type: "refinancing",
    });
  }, []);

  // Check if user already has an uploaded policy
  useEffect(() => {
    const checkPolicy = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const result = await insurancePolicyService.checkExistingPolicies(token);
        if (result && ((Array.isArray(result) && result.length > 0) || result.policy)) {
          setHasExistingPolicy(true);
        }
      } catch (err) {
        // Non-blocking
      }
    };
    if (user) checkPolicy();
  }, [user, getIdToken]);

  // Clear stale manual entry cache on mount
  useEffect(() => {
    sessionStorage.removeItem("vehicleManualEntry");
  }, []);

  // --- Data fetch (candidates) -----------------------------------------------
  useEffect(() => {
    const fetchVehicleCandidates = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = await getIdToken();
        if (!token) throw new Error("Authentication required");

        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/vehicle/candidates`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-user-token": token,
            },
          }
        );

        if (!response.ok)
          throw new Error(`Failed to fetch vehicles: ${response.status}`);

        const vehicleData = await response.json();
        let normalized = (Array.isArray(vehicleData) ? vehicleData : []).map(
          normalize
        );

        // Deduplicate by VIN — keep the entry with the most populated fields
        const deduped = (() => {
          const byVin = {};
          const noVin = [];
          for (const v of normalized) {
            if (!v.vin) {
              noVin.push(v);
              continue;
            }
            const key = v.vin.toUpperCase();
            if (!byVin[key]) {
              byVin[key] = v;
            } else {
              const score = (rec) =>
                [rec.year, rec.make, rec.model, rec.state, rec.plate, rec.mileage, rec.source]
                  .filter((f) => f != null && f !== "").length;
              if (score(v) > score(byVin[key])) {
                byVin[key] = v;
              }
            }
          }
          return [...Object.values(byVin), ...noVin];
        })();

        setVehicles(deduped);

        analyticsService.trackEvent("Refinancing Vehicles Loaded", {
          vehicle_count: deduped.length,
          raw_count: normalized.length,
          has_vehicles: deduped.length > 0,
          flow_type: "refinancing",
        });

        if (deduped.length === 0) {
          analyticsService.trackEvent("Refinancing No Vehicles Found", {
            screen: "vehicle_selection",
            flow_type: "refinancing",
          });
          setShowManualEntry(true);
        }
      } catch (err) {
        console.error("Error fetching vehicle candidates:", err);
        analyticsService.trackError("refi_vehicle_fetch_error", err, {
          screen: "vehicle_selection",
          flow_type: "refinancing",
        });
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchVehicleCandidates();
    } else {
      setIsLoading(false);
      setError("Please log in to view your vehicles");
    }
  }, [user, getIdToken]);

  // --- Caribou YMMT cascade loading ------------------------------------------

  // Load years when MAKE_MODEL_YEAR is selected
  useEffect(() => {
    if (inputMethod !== INPUT_METHODS.MAKE_MODEL_YEAR) return;

    const loadYears = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, years: true }));
        const token = await getIdToken();
        const years = await VehicleInfoService.fetchCaribouYears(token);
        setCaribouOptions((prev) => ({ ...prev, years }));
      } catch (err) {
        console.error("Error fetching Caribou years:", err);
      } finally {
        setLoadingStates((prev) => ({ ...prev, years: false }));
      }
    };

    if (user) loadYears();
  }, [inputMethod, user, getIdToken]);

  // Load makes when year changes
  useEffect(() => {
    if (inputMethod !== INPUT_METHODS.MAKE_MODEL_YEAR || !manualData.year) {
      setCaribouOptions((prev) => ({ ...prev, makes: [], models: [], trims: [] }));
      return;
    }

    const loadMakes = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, makes: true }));
        const token = await getIdToken();
        const makes = await VehicleInfoService.fetchCaribouMakes(manualData.year, token);
        setCaribouOptions((prev) => ({ ...prev, makes, models: [], trims: [] }));
      } catch (err) {
        console.error("Error fetching Caribou makes:", err);
      } finally {
        setLoadingStates((prev) => ({ ...prev, makes: false }));
      }
    };

    if (user) loadMakes();
  }, [inputMethod, manualData.year, user, getIdToken]);

  // Load models when make changes
  useEffect(() => {
    if (inputMethod !== INPUT_METHODS.MAKE_MODEL_YEAR || !manualData.year || !manualData.make) {
      setCaribouOptions((prev) => ({ ...prev, models: [], trims: [] }));
      return;
    }

    const loadModels = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, models: true }));
        const token = await getIdToken();
        const models = await VehicleInfoService.fetchCaribouModels(
          manualData.year,
          manualData.make,
          token
        );
        setCaribouOptions((prev) => ({ ...prev, models, trims: [] }));
      } catch (err) {
        console.error("Error fetching Caribou models:", err);
      } finally {
        setLoadingStates((prev) => ({ ...prev, models: false }));
      }
    };

    if (user) loadModels();
  }, [inputMethod, manualData.year, manualData.make, user, getIdToken]);

  // Load trims when model changes
  useEffect(() => {
    if (
      inputMethod !== INPUT_METHODS.MAKE_MODEL_YEAR ||
      !manualData.year ||
      !manualData.make ||
      !manualData.model
    ) {
      setCaribouOptions((prev) => ({ ...prev, trims: [] }));
      return;
    }

    const loadTrims = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, trims: true }));
        const token = await getIdToken();
        const trims = await VehicleInfoService.fetchCaribouTrims(
          manualData.year,
          manualData.make,
          manualData.model,
          token
        );
        setCaribouOptions((prev) => ({ ...prev, trims }));
      } catch (err) {
        console.error("Error fetching Caribou trims:", err);
      } finally {
        setLoadingStates((prev) => ({ ...prev, trims: false }));
      }
    };

    if (user) loadTrims();
  }, [inputMethod, manualData.year, manualData.make, manualData.model, user, getIdToken]);

  // --- Handlers -------------------------------------------------------------
  const handleVehicleSelect = (vehicle) => {
    analyticsService.trackEvent("Refinancing Vehicle Selected", {
      has_vin: !!vehicle.vin,
      has_mileage: vehicle.mileage != null,
      source: vehicle.source,
      flow_type: "refinancing",
    });
    setSelectedVehicle(vehicle);
    setInputMethod(null);
  };

  const handleContinue = async () => {
    if (!selectedVehicle) {
      setError("Please select a vehicle to continue");
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      analyticsService.trackEvent("Refinancing Button Click", {
        button_name: "continue-with-vehicle",
        screen: "vehicle_selection",
        vehicle_source: selectedVehicle?.source,
        flow_type: "refinancing",
      });
      sessionStorage.setItem(
        "selectedVehicle",
        JSON.stringify(selectedVehicle)
      );
      onComplete?.(selectedVehicle);
    } catch (err) {
      console.error("Error proceeding with selected vehicle:", err);
      setError("Failed to proceed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputMethodSelect = (method) => {
    setInputMethod(method);
    setSelectedVehicle(null);
    setManualErrors({});
    setManualData({
      vin: "",
      year: "",
      make: "",
      model: "",
      trim: "",
      nadaUid: "",
      licensePlate: "",
      state: "",
    });
    setCaribouOptions((prev) => ({
      ...prev,
      makes: [],
      models: [],
      trims: [],
    }));
  };

  const handleManualFieldChange = (field, value) => {
    setManualData((prev) => {
      const next = { ...prev, [field]: value };

      // Cascade resets for YMMT
      if (field === "year") {
        next.make = "";
        next.model = "";
        next.trim = "";
        next.nadaUid = "";
      } else if (field === "make") {
        next.model = "";
        next.trim = "";
        next.nadaUid = "";
      } else if (field === "model") {
        next.trim = "";
        next.nadaUid = "";
      }

      return next;
    });

    // Clear error for this field
    if (manualErrors[field]) {
      setManualErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleTrimSelect = (trimValue) => {
    // trimValue is the trim name; find the matching object for its uid
    const trimObj = caribouOptions.trims.find((t) => t.trim === trimValue);
    setManualData((prev) => ({
      ...prev,
      trim: trimValue,
      nadaUid: trimObj?.uid || "",
    }));
    if (manualErrors.trim) {
      setManualErrors((prev) => {
        const next = { ...prev };
        delete next.trim;
        return next;
      });
    }
  };

  const validateManualEntry = () => {
    const errors = {};

    if (inputMethod === INPUT_METHODS.VIN) {
      if (!manualData.vin.trim()) {
        errors.vin = "VIN is required";
      } else if (!VehicleInfoService.validateVIN(manualData.vin.trim())) {
        errors.vin = "VIN must be exactly 17 alphanumeric characters";
      }
    }

    if (inputMethod === INPUT_METHODS.MAKE_MODEL_YEAR) {
      if (!manualData.year) errors.year = "Year is required";
      if (!manualData.make) errors.make = "Make is required";
      if (!manualData.model) errors.model = "Model is required";
      if (!manualData.trim) errors.trim = "Trim is required";
    }

    if (inputMethod === INPUT_METHODS.LICENSE_PLATE) {
      if (!manualData.licensePlate.trim())
        errors.licensePlate = "License plate is required";
      if (!manualData.state) errors.state = "State is required";
    }

    setManualErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleManualContinue = async () => {
    if (!validateManualEntry()) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const manualVehicle = {
        vin: manualData.vin.trim() || null,
        year: manualData.year ? parseInt(manualData.year) : null,
        make: manualData.make || null,
        model: manualData.model || null,
        trim: manualData.trim || null,
        nadaUid: manualData.nadaUid || null,
        plate: manualData.licensePlate.trim() || null,
        state: manualData.state || null,
        mileage: null,
        source: "MANUAL",
      };

      analyticsService.trackEvent("Refinancing Manual Vehicle Entry", {
        input_method: inputMethod,
        has_vin: !!manualVehicle.vin,
        has_ymmt: !!(manualVehicle.year && manualVehicle.make && manualVehicle.model && manualVehicle.trim),
        has_plate: !!manualVehicle.plate,
        has_nada_uid: !!manualVehicle.nadaUid,
        flow_type: "refinancing",
      });

      sessionStorage.setItem(
        "selectedVehicle",
        JSON.stringify(manualVehicle)
      );
      onComplete?.(manualVehicle);
    } catch (err) {
      console.error("Error proceeding with manual vehicle:", err);
      setError("Failed to proceed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Manual Entry Forms ---------------------------------------------------
  const renderVinForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Vehicle Identification Number (VIN)
        </label>
        <input
          type="text"
          maxLength={17}
          value={manualData.vin}
          onChange={(e) =>
            handleManualFieldChange("vin", e.target.value.toUpperCase())
          }
          placeholder="e.g. 1HGBH41JXMN109186"
          className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            manualErrors.vin ? "border-red-400" : "border-gray-300"
          }`}
        />
        {manualErrors.vin && (
          <p className="text-xs text-red-500 mt-1">{manualErrors.vin}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          17-character VIN found on your registration or insurance card
        </p>
      </div>
    </div>
  );

  const renderSelectWithLoader = (label, value, onChange, options, placeholder, loadingPlaceholder, errorKey, disabled) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`w-full px-4 py-3 border rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            manualErrors[errorKey] ? "border-red-400" : "border-gray-300"
          } ${disabled ? "bg-gray-50 text-gray-400" : ""}`}
        >
          <option value="">
            {disabled ? loadingPlaceholder : placeholder}
          </option>
          {options.map((opt) => {
            const val = typeof opt === "object" ? (opt.name || opt.trim || opt) : opt;
            const display = typeof opt === "object" ? (opt.name || opt.trim || opt) : opt;
            return (
              <option key={val} value={val}>
                {display}
              </option>
            );
          })}
        </select>
        {disabled && typeof disabled === "boolean" && value === "" ? (
          <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin pointer-events-none" />
        ) : (
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        )}
      </div>
      {manualErrors[errorKey] && (
        <p className="text-xs text-red-500 mt-1">{manualErrors[errorKey]}</p>
      )}
    </div>
  );

  const renderYMMTForm = () => (
    <div className="space-y-4">
      {/* Year */}
      {renderSelectWithLoader(
        "Year",
        manualData.year,
        (e) => handleManualFieldChange("year", e.target.value),
        caribouOptions.years,
        "Select year...",
        loadingStates.years ? "Loading years..." : "Select year...",
        "year",
        loadingStates.years
      )}

      {/* Make */}
      {renderSelectWithLoader(
        "Make",
        manualData.make,
        (e) => handleManualFieldChange("make", e.target.value),
        caribouOptions.makes,
        "Select make...",
        loadingStates.makes ? "Loading makes..." : (!manualData.year ? "Select a year first..." : "Select make..."),
        "make",
        loadingStates.makes || !manualData.year
      )}

      {/* Model */}
      {renderSelectWithLoader(
        "Model",
        manualData.model,
        (e) => handleManualFieldChange("model", e.target.value),
        caribouOptions.models,
        "Select model...",
        loadingStates.models ? "Loading models..." : (!manualData.make ? "Select a make first..." : "Select model..."),
        "model",
        loadingStates.models || !manualData.make
      )}

      {/* Trim */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Trim
        </label>
        <div className="relative">
          <select
            value={manualData.trim}
            onChange={(e) => handleTrimSelect(e.target.value)}
            disabled={loadingStates.trims || !manualData.model}
            className={`w-full px-4 py-3 border rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              manualErrors.trim ? "border-red-400" : "border-gray-300"
            } ${loadingStates.trims || !manualData.model ? "bg-gray-50 text-gray-400" : ""}`}
          >
            <option value="">
              {loadingStates.trims
                ? "Loading trims..."
                : !manualData.model
                ? "Select a model first..."
                : "Select trim..."}
            </option>
            {caribouOptions.trims.map((t) => (
              <option key={t.uid || t.trim} value={t.trim}>
                {t.trim}
              </option>
            ))}
          </select>
          {loadingStates.trims ? (
            <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin pointer-events-none" />
          ) : (
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          )}
        </div>
        {manualErrors.trim && (
          <p className="text-xs text-red-500 mt-1">{manualErrors.trim}</p>
        )}
      </div>
    </div>
  );

  const renderLicensePlateForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          License Plate Number
        </label>
        <input
          type="text"
          value={manualData.licensePlate}
          onChange={(e) =>
            handleManualFieldChange(
              "licensePlate",
              e.target.value.toUpperCase()
            )
          }
          placeholder="e.g. ABC 1234"
          className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            manualErrors.licensePlate ? "border-red-400" : "border-gray-300"
          }`}
        />
        {manualErrors.licensePlate && (
          <p className="text-xs text-red-500 mt-1">
            {manualErrors.licensePlate}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          State
        </label>
        <div className="relative">
          <select
            value={manualData.state}
            onChange={(e) => handleManualFieldChange("state", e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              manualErrors.state ? "border-red-400" : "border-gray-300"
            }`}
          >
            <option value="">Select state...</option>
            {MOCK_US_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.text}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        {manualErrors.state && (
          <p className="text-xs text-red-500 mt-1">{manualErrors.state}</p>
        )}
      </div>
    </div>
  );

  // --- Input Method Cards ---------------------------------------------------
  const renderInputMethodCards = () => {
    const methods = [
      // TODO: Re-enable once Caribou YMMT API is unblocked
      // {
      //   id: INPUT_METHODS.MAKE_MODEL_YEAR,
      //   icon: <ListChecks className="w-5 h-5" />,
      //   title: "Year / Make / Model / Trim",
      //   description: "Select your vehicle from Caribou's database",
      // },
      {
        id: INPUT_METHODS.LICENSE_PLATE,
        icon: <CreditCard className="w-5 h-5" />,
        title: "License Plate & State",
        description: "Enter your plate number and state",
      },
      {
        id: INPUT_METHODS.VIN,
        icon: <KeyRound className="w-5 h-5" />,
        title: "Enter VIN",
        description: "17-character Vehicle Identification Number",
      },
    ];

    return (
      <div className="space-y-4">
        {methods.map((m) => {
          const isActive = inputMethod === m.id;
          return (
            <div key={m.id}>
              <button
                type="button"
                onClick={() => handleInputMethodSelect(m.id)}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                  isActive
                    ? "border-[#7B61FF] bg-[#F5F2FF]"
                    : "border-[#7B61FF]/30 bg-[#F5F2FF]/50 hover:border-[#7B61FF]/60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isActive
                        ? "bg-[#7B61FF] text-white"
                        : "bg-[#7B61FF]/10 text-[#7B61FF]"
                    }`}
                  >
                    {m.icon}
                  </div>
                  <div>
                    <div className="font-bold text-darkPurple">{m.title}</div>
                    <div className="text-sm text-gray-500">
                      {m.description}
                    </div>
                  </div>
                </div>
              </button>

              {/* Render form inline below the selected method */}
              {isActive && (
                <div className="mt-3 p-4 bg-[#F5F2FF] rounded-xl border border-[#7B61FF]/20">
                  {m.id === INPUT_METHODS.VIN && renderVinForm()}
                  {m.id === INPUT_METHODS.MAKE_MODEL_YEAR && renderYMMTForm()}
                  {m.id === INPUT_METHODS.LICENSE_PLATE &&
                    renderLicensePlateForm()}
                </div>
              )}
            </div>
          );
        })}

        {inputMethod && (
          <button
            onClick={handleManualContinue}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  // --- UI States ------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Car className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Loading Your Vehicles
          </h1>
          <p className="text-gray-600">
            We're checking for vehicles in your account...
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 flex justify-center">
            <div className="flex items-center gap-2">
              <Loader className="w-5 h-5 animate-spin text-blue-500" />
              <span className="text-gray-600">
                Fetching vehicle information...
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && vehicles.length === 0 && !showManualEntry) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Something Went Wrong
          </h1>
          <p className="text-gray-600">
            We encountered an error while loading your vehicles.
          </p>
        </div>

        <Card className="border-red-100 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error Details</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <ExtendedWarrantyButton
          onClick={() => window.location.reload()}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </ExtendedWarrantyButton>
      </div>
    );
  }

  // No vehicles found
  if (vehicles.length === 0) {
    return (
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-bold font-bebas uppercase">
            You're one step away from your refinancing offer.
          </h1>
        </div>

        {/* Green card */}
        <div className="bg-[#D4F6C9] rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wide font-bold mb-3 text-gray-600">
            What You Unlock
          </p>
          <div className="flex items-stretch">
            <div className="flex-1 pr-4">
              <p className="text-3xl font-bold font-bebas text-darkPurple">
                $3
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Cash earned — your data, your money
              </p>
            </div>
            <div className="border-l border-gray-400 pl-4 flex-1">
              <p className="text-3xl font-bold font-bebas text-darkPurple">$1,000+</p>
              <p className="text-sm text-gray-600 mt-1">
                Estimated potential annual savings
              </p>
            </div>
          </div>
        </div>

        {/* Upload card — hide if policy already uploaded */}
        {!hasExistingPolicy && (
          <>
            <div className="border-2 border-[#7B61FF] rounded-2xl p-5 space-y-3 bg-[#F5F2FF]">
              <div className="flex items-center gap-2">
                <div className="bg-[#D4F6C9] rounded-full px-3 py-1">
                  <span className="text-sm font-bold text-darkPurple">Earn $3</span>
                </div>
                <p className="text-[#7B61FF] font-bold text-sm uppercase tracking-wide">
                  Recommended
                </p>
              </div>
              <h2 className="text-lg font-bold text-darkPurple">
                Upload your insurance card or declaration page
              </h2>
              <p className="text-gray-500 text-sm">
                Takes 30 seconds. Upload your document to earn a $3 cash reward and get a personalized refinancing offer.
              </p>

              {onNoVehicles && (
                <button
                  onClick={() => {
                    analyticsService.trackEvent("Refinancing Button Click", {
                      button_name: "upload-dec-no-results",
                      screen: "vehicle_selection",
                      flow_type: "refinancing",
                    });
                    onNoVehicles();
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
                >
                  <Camera className="w-5 h-5" />
                  Upload & Earn $3
                </button>
              )}
            </div>

            {/* See a sample link */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setOpenPreviewModal(true)}
                className="underline text-[#1B1B1E] text-sm"
              >
                See a sample
              </button>
            </div>

            <DocumentPreviewModal
              open={openPreviewModal}
              onOpenChange={setOpenPreviewModal}
            />
          </>
        )}

        {/* Manual entry section */}
        <div className="text-center space-y-1">
          <p className="text-gray-700 font-bold text-base">
            {hasExistingPolicy ? "Enter your vehicle information" : "Don't have your insurance document handy?"}
          </p>
          <p className="text-gray-500 text-sm">
            {hasExistingPolicy
              ? "Enter your vehicle information manually."
              : "Enter your vehicle information manually instead. No cash reward for manual entry."}
          </p>
        </div>

        {renderInputMethodCards()}
      </div>
    );
  }

  // Vehicles found — list + manual entry options
  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="text-center space-y-3">
        <h1 className="text-2xl font-bold font-bebas uppercase">
          Select Your Vehicle
        </h1>
        <p className="text-gray-600 text-sm">
          We found {vehicles.length} vehicle{vehicles.length > 1 ? "s" : ""} in
          your account.
        </p>
      </div>

      {/* Vehicle cards */}
      <div className="space-y-3">
        {vehicles.map((n, idx) => {
          const isSelected = sameVehicle(selectedVehicle, n) && !inputMethod;
          const title = titleFor(n);
          return (
            <button
              type="button"
              key={keyFor(n, idx)}
              className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                isSelected
                  ? "border-[#7B61FF] bg-[#F5F2FF]"
                  : "border-gray-200 hover:border-[#7B61FF]/40 hover:bg-[#F5F2FF]/30"
              }`}
              onClick={() => handleVehicleSelect(n)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isSelected
                      ? "border-[#7B61FF] bg-[#7B61FF]"
                      : "border-gray-300"
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-darkPurple">
                    {title}
                  </h3>

                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-500">
                    {(n.plate || n.state) && (
                      <div className="flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        <span>{n.plate || "No plate"}</span>
                      </div>
                    )}

                    {n.state && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span>{n.state}</span>
                      </div>
                    )}

                    {n.mileage != null && (
                      <div className="flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        <span>{n.mileage.toLocaleString()} mi</span>
                      </div>
                    )}

                    {n.vin && (
                      <div className="col-span-2 mt-1">
                        <span className="text-xs text-gray-400">VIN </span>
                        <span className="text-xs break-all select-all">{n.vin}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Continue with selected candidate */}
      {selectedVehicle && !inputMethod && (
        <>
          {error && !isLoading && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          <button
            onClick={handleContinue}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </>
      )}

      {/* Don't see your vehicle section */}
      <div className="text-center space-y-1 pt-2">
        <p className="text-gray-700 font-bold text-base">Don't see your vehicle?</p>
        <p className="text-gray-500 text-sm">
          {hasExistingPolicy
            ? "Enter your vehicle info manually below."
            : "Upload your insurance document or enter info manually."}
        </p>
      </div>

      {/* Upload DEC card — hide if policy already uploaded */}
      {!hasExistingPolicy && onNoVehicles && (
        <div className="border-2 border-[#7B61FF] rounded-2xl p-5 space-y-3 bg-[#F5F2FF]">
          <div className="flex items-center gap-2">
            <div className="bg-[#D4F6C9] rounded-full px-3 py-1">
              <span className="text-sm font-bold text-darkPurple">Earn $3</span>
            </div>
            <p className="text-[#7B61FF] font-bold text-sm uppercase tracking-wide">
              Recommended
            </p>
          </div>
          <p className="text-gray-500 text-sm">
            Upload your declaration page to earn a $3 cash reward. We'll extract your vehicle info automatically.
          </p>
          <button
            onClick={() => {
              analyticsService.trackEvent("Refinancing Button Click", {
                button_name: "upload-dec-from-list",
                screen: "vehicle_selection",
                flow_type: "refinancing",
              });
              onNoVehicles();
            }}
            className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30"
          >
            <Camera className="w-5 h-5" />
            Upload & Earn $3
          </button>
        </div>
      )}

      {/* Manual entry section */}
      <div className="text-center space-y-1">
        <p className="text-gray-700 font-bold text-base">
          {hasExistingPolicy ? "Enter vehicle info" : "Or enter vehicle info manually"}
        </p>
        {!hasExistingPolicy && (
          <p className="text-gray-400 text-xs">
            No cash reward for manual entry
          </p>
        )}
      </div>

      {renderInputMethodCards()}

      <p className="text-xs text-center text-gray-400">
        Showing {vehicles.length} vehicle{vehicles.length > 1 ? "s" : ""} from
        your account
      </p>
    </div>
  );
};

export default RefinancingVehicleSelection;
